/**
 * Workspace Item Card Component
 *
 * Displays a table, linked table, or folder as a compact card.
 * Part of the Data Workspace OS feature set.
 *
 * Design: Simple icon-focused cards with name and count/type indicator.
 * Right-click context menu for actions.
 *
 * Interaction Model:
 * - Single click: Select + open preview (after 250ms delay)
 * - Double click: Navigate to item
 * - Cmd/Ctrl+click: Toggle selection (multi-select)
 * - Shift+click: Range selection
 * - Drag & drop: Move items into folders
 * - Draft tables: Any click resumes creation
 */

import { useCallback, useRef, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { AlertCircle, CircleSlash, Plug } from 'lucide-react';
import { ContextMenu, ContextMenuTrigger } from '@loader/ui';
import type { WorkspaceItemType, ConnectorType } from '@loader/shared-types';

import { ItemContextMenu } from './ItemContextMenu';
import { useSelectionStore, useSidePanelStore } from '../stores';
import { useRenameLogic } from '../hooks';
import { CLICK_DELAY_MS } from '../constants';
import { ConnectorLabel } from './ConnectorLabel';

import { WorkspaceIcon } from '@/components/icons';
import type { TableStatus } from '@/lib/types';

// ============================================================================
// TYPES
// ============================================================================

export interface WorkspaceItemCardProps {
  id: string;
  type: WorkspaceItemType;
  name: string;
  description?: string | null;
  isFavorite?: boolean;
  itemCount?: number;
  versionCount?: number;
  updatedAt?: string;
  createdAt?: string;
  viewMode?: 'grid' | 'list';
  status?: TableStatus;
  connectorType?: ConnectorType | null;
  isTrash?: boolean;
  /** Whether user can edit (editor/owner) - viewers cannot modify items */
  canEdit?: boolean;
  isSelected?: boolean;

  /** All item IDs in current view (required for Shift+click range selection) */
  allItemIds?: string[];

  /** Action handlers */
  onDuplicate?: (id: string) => void;
  onToggleFavorite?: (id: string, type: WorkspaceItemType) => void;
  onMove?: (id: string, type: WorkspaceItemType) => void;
  onDelete?: (id: string, type: WorkspaceItemType, name?: string) => void;
  onRestore?: (id: string, type: WorkspaceItemType) => void;
  onDeletePermanently?: (id: string, type: WorkspaceItemType) => void;
  onPreview?: (id: string, type: WorkspaceItemType) => void;
  onResumeDraft?: (id: string) => void;

  /** Drag and drop handlers */
  onDragStart?: (e: React.DragEvent, id: string, type: WorkspaceItemType) => void;
  onDragEnd?: () => void;
  onDragOver?: (e: React.DragEvent, id: string) => void;
  onDragLeave?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent, id: string) => void;
  isDragging?: boolean;
  isDropTarget?: boolean;
}

// ============================================================================
// GRID CARD COMPONENT
// ============================================================================

/**
 * Grid view representation of a workspace item
 * Compact card with centered icon and name
 */
function GridCard({
  id,
  type,
  name,
  isFavorite = false,
  itemCount,
  versionCount,
  status,
  connectorType,
  isTrash,
  canEdit = true,
  isSelected,
  selectedCount,
  onDuplicate,
  onToggleFavorite,
  onMove,
  onDelete,
  onRestore,
  onDeletePermanently,
  onClick,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  isDragging,
  isDropTarget,
}: WorkspaceItemCardProps & { onClick: (e: React.MouseEvent) => void; selectedCount: number }) {
  // Rename logic - disabled for viewers
  const {
    inputRef,
    isRenaming,
    renameValue,
    setRenameValue,
    handleStartRename,
    handleSaveRename,
    handleKeyDown,
  } = useRenameLogic(id, type, name, canEdit);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Get subtitle text based on item type
   * - Folders: "X items"
   * - Tables: "X ver."
   */
  const getSubtitle = (): string => {
    if (type === 'folder' && itemCount !== undefined) {
      return `${itemCount} item${itemCount !== 1 ? 's' : ''}`;
    }
    if (type === 'table' && versionCount !== undefined) {
      return `${versionCount} ver.`;
    }
    return '';
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          data-workspace-item
          className={`group relative flex w-24 cursor-pointer flex-col items-center rounded-lg p-2 transition-all ${
            !isSelected ? 'hover:bg-muted/50' : ''
          } ${isDragging ? 'opacity-40' : ''} ${isDropTarget ? 'bg-accent ring-2 ring-primary' : ''} ${!canEdit ? 'cursor-default' : ''}`}
          onClick={isRenaming ? undefined : onClick}
          draggable={!isRenaming && canEdit}
          onDragStart={(e) => !isRenaming && canEdit && onDragStart?.(e, id, type)}
          onDragEnd={onDragEnd}
          onDragOver={(e) => type === 'folder' && canEdit && onDragOver?.(e, id)}
          onDragLeave={(e) => type === 'folder' && onDragLeave?.(e)}
          onDrop={(e) => type === 'folder' && canEdit && onDrop?.(e, id)}
        >
          {/* Icon container with status badge */}
          <div
            className={`relative rounded-lg p-2 transition-all duration-150 group-hover:scale-105 ${
              isSelected ? 'bg-muted-foreground/20 dark:bg-muted-foreground/30' : ''
            }`}
          >
            <WorkspaceIcon type={type} size="md" />

            {/* Status badge - draft or disabled */}
            {status === 'draft' && (
              <div className="absolute -right-0.5 -top-0.5 rounded-full bg-background p-0.5">
                <AlertCircle className="size-3 text-amber-500" />
              </div>
            )}
            {status === 'disabled' && (
              <div className="absolute -right-0.5 -top-0.5 rounded-full bg-background p-0.5">
                <CircleSlash className="size-3 text-muted-foreground" />
              </div>
            )}
            {connectorType && (
              <div className="absolute -bottom-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30 ring-1 ring-background">
                <Plug className="size-2.5 text-blue-600 dark:text-blue-400" />
              </div>
            )}
          </div>

          {/* Name - editable input or text */}
          {isRenaming ? (
            <input
              ref={inputRef}
              type="text"
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleSaveRename}
              onClick={(e) => e.stopPropagation()}
              className="mt-2 w-full rounded bg-transparent px-1 text-center text-sm font-medium outline-none ring-1 ring-primary"
            />
          ) : (
            <p
              className={`mt-2 w-full text-center text-sm font-medium ${
                isSelected
                  ? 'truncate rounded bg-primary px-1.5 py-0.5 text-primary-foreground'
                  : 'truncate'
              }`}
            >
              {name}
            </p>
          )}

          {/* Subtitle (item count or version count) */}
          {!isRenaming && getSubtitle() && (
            <p className="text-xs text-muted-foreground">{getSubtitle()}</p>
          )}
        </div>
      </ContextMenuTrigger>

      {/* Context menu */}
      <ItemContextMenu
        id={id}
        type={type}
        name={name}
        isFavorite={isFavorite}
        isTrash={isTrash}
        selectedCount={selectedCount}
        canEdit={canEdit}
        onRename={handleStartRename}
        onDuplicate={onDuplicate}
        onToggleFavorite={onToggleFavorite}
        onMove={onMove}
        onDelete={onDelete}
        onRestore={onRestore}
        onDeletePermanently={onDeletePermanently}
      />
    </ContextMenu>
  );
}

// ============================================================================
// LIST ROW COMPONENT
// ============================================================================

/**
 * List view representation of a workspace item
 * Horizontal row with icon, name, type, and date
 */
function ListRow({
  id,
  type,
  name,
  isFavorite = false,
  updatedAt,
  status,
  connectorType,
  isTrash,
  canEdit = true,
  isSelected,
  selectedCount,
  onDuplicate,
  onToggleFavorite,
  onMove,
  onDelete,
  onRestore,
  onDeletePermanently,
  onClick,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDragLeave,
  onDrop,
  isDragging,
  isDropTarget,
}: WorkspaceItemCardProps & { onClick: (e: React.MouseEvent) => void; selectedCount: number }) {
  // Rename logic - disabled for viewers
  const {
    inputRef,
    isRenaming,
    renameValue,
    setRenameValue,
    handleStartRename,
    handleSaveRename,
    handleKeyDown,
  } = useRenameLogic(id, type, name, canEdit);

  // ============================================================================
  // HELPER FUNCTIONS
  // ============================================================================

  /**
   * Get human-readable type label
   */
  const getTypeLabel = (): string => {
    switch (type) {
      case 'table':
        return 'Table';
      case 'linked_table':
        return 'Linked Table';
      case 'folder':
        return 'Folder';
      default:
        return 'Item';
    }
  };

  /**
   * Format date string to readable format
   */
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          data-workspace-item
          className={`group flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 transition-all ${
            isSelected ? 'bg-primary text-primary-foreground' : 'hover:bg-muted/50'
          } ${isDragging ? 'opacity-40' : ''} ${isDropTarget ? 'bg-accent ring-2 ring-primary' : ''} ${!canEdit ? 'cursor-default' : ''}`}
          onClick={isRenaming ? undefined : onClick}
          draggable={!isRenaming && canEdit}
          onDragStart={(e) => !isRenaming && canEdit && onDragStart?.(e, id, type)}
          onDragEnd={onDragEnd}
          onDragOver={(e) => type === 'folder' && canEdit && onDragOver?.(e, id)}
          onDragLeave={(e) => type === 'folder' && onDragLeave?.(e)}
          onDrop={(e) => type === 'folder' && canEdit && onDrop?.(e, id)}
        >
          {/* Icon container with status badge */}
          <div className="relative shrink-0 transition-transform duration-150 group-hover:scale-110">
            <WorkspaceIcon type={type} size="sm" />

            {/* Status badge - draft or disabled */}
            {status === 'draft' && (
              <div className="absolute -right-1 -top-1 rounded-full bg-background p-0.5">
                <AlertCircle className="size-2.5 text-amber-500" />
              </div>
            )}
            {status === 'disabled' && (
              <div className="absolute -right-1 -top-1 rounded-full bg-background p-0.5">
                <CircleSlash className="size-2.5 text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Name and type section */}
          <div className="min-w-0 flex-1">
            {isRenaming ? (
              <input
                ref={inputRef}
                type="text"
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleSaveRename}
                onClick={(e) => e.stopPropagation()}
                className="mb-0.5 w-full rounded bg-transparent px-1 text-sm font-medium outline-none ring-1 ring-primary"
              />
            ) : (
              <div className="flex items-center gap-1.5 min-w-0">
                <p className="truncate text-sm font-medium">{name}</p>
                {connectorType && (
                  <ConnectorLabel connectorType={connectorType} isSelected={isSelected} />
                )}
              </div>
            )}
            <p
              className={`text-xs ${isSelected ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}
            >
              {getTypeLabel()}
            </p>
          </div>

          {/* Updated date */}
          {!isRenaming && updatedAt && (
            <div
              className={`hidden text-xs md:block ${isSelected ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}
            >
              {formatDate(updatedAt)}
            </div>
          )}
        </div>
      </ContextMenuTrigger>

      {/* Context menu */}
      <ItemContextMenu
        id={id}
        type={type}
        name={name}
        isFavorite={isFavorite}
        isTrash={isTrash}
        selectedCount={selectedCount}
        canEdit={canEdit}
        onRename={handleStartRename}
        onDuplicate={onDuplicate}
        onToggleFavorite={onToggleFavorite}
        onMove={onMove}
        onDelete={onDelete}
        onRestore={onRestore}
        onDeletePermanently={onDeletePermanently}
      />
    </ContextMenu>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Main WorkspaceItemCard component
 * Handles interaction logic and delegates rendering to GridCard or ListRow
 */
export function WorkspaceItemCard(props: WorkspaceItemCardProps) {
  const {
    id,
    type,
    name,
    description,
    isFavorite,
    updatedAt,
    createdAt,
    viewMode = 'grid',
    status,
    connectorType,
    allItemIds = [],
    onPreview,
    onResumeDraft,
  } = props;

  const navigate = useNavigate();
  const clickTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Selection store - single source of truth for selection state
  const { selectItem, toggleSelect, selectRange, selectedIds } = useSelectionStore();

  // Calculate selected count for multi-select context menu
  // Only apply multi-select behavior if this item is selected
  const selectedCount = props.isSelected ? selectedIds.size : 1;

  // Side panel store for double-click expand and closing on multi-select
  const { openPreview, expand } = useSidePanelStore();

  // ============================================================================
  // CLEANUP ON UNMOUNT
  // ============================================================================

  /**
   * Clear click timeout on unmount to prevent memory leaks
   */
  useEffect(() => {
    return () => {
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;
      }
    };
  }, []);

  // ============================================================================
  // SELECTION LOGIC
  // ============================================================================

  /**
   * Handle item selection with modifier key support
   * - Normal click: Select single item
   * - Cmd/Ctrl+click: Toggle selection (multi-select)
   * - Shift+click: Range selection from last selected item
   */
  const handleSelect = useCallback(
    (e: React.MouseEvent) => {
      if (e.shiftKey) {
        // Shift+click: Select range from last selected item to this item
        selectRange(id, allItemIds);
      } else if (e.metaKey || e.ctrlKey) {
        // Cmd/Ctrl+click: Toggle this item's selection
        toggleSelect(id);
      } else {
        // Normal click: Select only this item
        selectItem(id);
      }
    },
    [id, allItemIds, selectItem, toggleSelect, selectRange]
  );

  // ============================================================================
  // NAVIGATION LOGIC
  // ============================================================================

  /**
   * Get navigation link properties for folder items
   * Tables and linked tables use the overlay pattern instead
   */
  const getFolderLinkProps = useCallback(() => {
    return { to: '/workspace/folder/$id' as const, params: { id } };
  }, [id]);

  // ============================================================================
  // CLICK HANDLING
  // ============================================================================

  /**
   * Handle click with timeout to distinguish single vs double click
   *
   * Behavior:
   * - Draft tables: Any click immediately resumes creation
   * - Single click: Select item + open preview after 250ms
   * - Double click (before timeout):
   *   - Folders: Navigate to folder view
   *   - Tables/Linked Tables: Expand preview overlay
   */
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Special case: Draft tables - any click resumes the creation process
      if (type === 'table' && status === 'draft' && onResumeDraft) {
        if (clickTimeoutRef.current) {
          clearTimeout(clickTimeoutRef.current);
          clickTimeoutRef.current = null;
        }
        handleSelect(e);
        onResumeDraft(id);
        return;
      }

      // If there's a pending timeout, this is a double click
      if (clickTimeoutRef.current) {
        clearTimeout(clickTimeoutRef.current);
        clickTimeoutRef.current = null;

        // Folders: navigate to folder view
        if (type === 'folder') {
          void navigate(getFolderLinkProps());
          return;
        }

        // Tables/Linked Tables: expand preview overlay
        openPreview({
          id,
          type,
          name,
          description,
          isFavorite,
          updatedAt,
          createdAt,
          connectorType,
        });
        expand();
        return;
      }

      // Immediately select the item (macOS Finder-style)
      handleSelect(e);

      // Only open preview on normal click (no modifiers)
      // Multi-select (Cmd/Ctrl+click or Shift+click) should not trigger preview
      if (!e.metaKey && !e.ctrlKey && !e.shiftKey) {
        // Set timeout for single click action (open preview)
        clickTimeoutRef.current = setTimeout(() => {
          clickTimeoutRef.current = null;
          onPreview?.(id, type);
        }, CLICK_DELAY_MS);
      }
    },
    [id, type, name, description, isFavorite, updatedAt, createdAt, connectorType, status, navigate, getFolderLinkProps, handleSelect, onPreview, onResumeDraft, openPreview, expand]
  );

  // ============================================================================
  // RENDER
  // ============================================================================

  // Delegate to appropriate view component
  if (viewMode === 'list') {
    return <ListRow {...props} onClick={handleClick} selectedCount={selectedCount} />;
  }

  return <GridCard {...props} onClick={handleClick} selectedCount={selectedCount} />;
}
